import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type FlatList, type ListRenderItemInfo, View } from 'react-native';
import Animated, { FadeIn, ZoomIn, ZoomOut } from 'react-native-reanimated';

import { Box, Heading, Text } from '@squire/ui';

import { InfoCircleIcon } from '@/components/_icons';
import type { Service } from '@/types/booking';
import { getUngroupedServices } from '@/utils/booking/getUngroupedServices';
import { isAddonService } from '@/utils/isAddonService';

import {
  CategorySelect,
  UNCATEGORIZED_CATEGORY_ID,
} from '../CategorySelect/CategorySelect';
import { GiftCardOption } from '../GiftCard';
import { ServiceCardV2 } from '../ServiceCardV2/ServiceCardV2';
import { ServiceGridSkeletonV2 } from '../ServiceGridSkeletonV2';
import { ServiceGroupHeading } from '../ServiceGroupHeading';
import { styles } from './styles';
import type { ServiceListV2Item, ServiceListV2Props } from './types';

const AnimatedBox = Animated.createAnimatedComponent(Box);

const ADDON_CATEGORY_ID = '__sqr_internal_addons_v1__';

export function ServiceListV2({
  services,
  categories,
  selectedCategoryId,
  selectedServiceId,
  onServiceSelect,
  onCategoryChange,
  onPrimaryRemoveWithAddons,
  isLoading = false,
  error = null,
  isSelecting: _isSelecting = false,
  additionalServicesIds,
  allowMultipleServices = true,
  testID = 'service-grid',
  style,
  currency,
  giftCardEnabled,
  onGiftCardPress,
  hideCategorySelect = false,
  groups: groupsProp,
  getCategoryName,
  showPriceRangeTooltip = false,
  ...props
}: ServiceListV2Props) {
  const { t } = useTranslation('common', { keyPrefix: 'ServiceGrid' });
  const groups = groupsProp?.length ? groupsProp : undefined;

  // The gift card row's entrance fade-in is meant for the initial cascade when
  // the list first loads with nothing selected. Once the shopper has selected
  // a service at least once, the gift card reappearing (e.g. after deselecting)
  // is a direct result of that interaction, not a fresh page load, so it should
  // render immediately instead of waiting out a delay based on its position.
  const hasSelectedServiceRef = useRef(!!selectedServiceId);
  useEffect(() => {
    if (selectedServiceId) {
      hasSelectedServiceRef.current = true;
    }
  }, [selectedServiceId]);

  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      if (service.addonOnly) return false;
      // Non-standalone services (0 min or $0, including ranges starting at 0)
      // are add-ons: valid only as secondary selections on top of a primary.
      // Single-select shops/barbers have no add-on concept (tapping replaces
      // the primary), so hide them entirely there.
      const isAddon = isAddonService(service);
      if (isAddon && !allowMultipleServices) return false;
      if (selectedCategoryId === ADDON_CATEGORY_ID) {
        if (!selectedServiceId) return false;
        return isAddon;
      }
      if (!selectedServiceId && isAddon) return false;
      if (selectedCategoryId === UNCATEGORIZED_CATEGORY_ID)
        return !service.categories?.length;
      if (selectedCategoryId)
        return service.categories?.some((cat) => cat.id === selectedCategoryId);
      return true;
    });
  }, [services, selectedCategoryId, selectedServiceId, allowMultipleServices]);

  const primaryServiceIds = useMemo(() => {
    if (!selectedServiceId) return [];
    const ids: string[] = [selectedServiceId];
    (additionalServicesIds ?? []).forEach((id) => {
      const svc = services.find((s) => s.id === id);
      if (svc && !isAddonService(svc)) ids.push(id);
    });
    return ids;
  }, [selectedServiceId, additionalServicesIds, services]);

  const primarySectionServices = useMemo(
    () =>
      primaryServiceIds
        .map((id) => services.find((s) => s.id === id))
        .filter((s): s is Service => !!s),
    [primaryServiceIds, services],
  );

  const addonServiceIds = useMemo(
    () =>
      (additionalServicesIds ?? []).filter((id) => {
        const svc = services.find((s) => s.id === id);
        return svc && isAddonService(svc);
      }),
    [additionalServicesIds, services],
  );

  const addonSectionServices = useMemo(
    () =>
      addonServiceIds
        .map((id) => services.find((s) => s.id === id))
        .filter((s): s is Service => !!s),
    [addonServiceIds, services],
  );

  const gridServices = useMemo(() => {
    const excludeSet = new Set([...primaryServiceIds, ...addonServiceIds]);
    return filteredServices.filter((service) => !excludeSet.has(service.id));
  }, [filteredServices, primaryServiceIds, addonServiceIds]);

  const gridServiceIds = useMemo(
    () => new Set(gridServices.map(({ id }) => id)),
    [gridServices],
  );

  const handlePrimarySectionPress = useCallback(
    (service: Service) => {
      const remaining = primarySectionServices.filter(
        (s) => s.id !== service.id,
      ).length;
      if (remaining === 0 && addonSectionServices.length > 0) {
        onPrimaryRemoveWithAddons?.(addonSectionServices);
      } else {
        onServiceSelect(service);
      }
    },
    [
      primarySectionServices,
      addonSectionServices,
      onPrimaryRemoveWithAddons,
      onServiceSelect,
    ],
  );

  const augmentedCategories = useMemo(() => {
    if (!selectedServiceId || !allowMultipleServices) return categories;
    const hasAddonServices = services.some(
      (s) => !s.addonOnly && isAddonService(s),
    );
    if (!hasAddonServices) return categories;
    return [
      ...categories,
      { id: ADDON_CATEGORY_ID, name: t('addonCategoryLabel') },
    ];
  }, [services, categories, selectedServiceId, allowMultipleServices, t]);

  // With the category fixed by the route, the section names it.
  const fixedCategoryName = hideCategorySelect
    ? categories.find(({ id }) => id === selectedCategoryId)?.name
    : undefined;

  const gridData = useMemo((): ServiceListV2Item[] => {
    const rows: ServiceListV2Item[] = groups
      ? [
          ...groups.flatMap(({ group, services: groupServices }) => {
            const visible = groupServices.filter(({ id }) =>
              gridServiceIds.has(id),
            );
            return visible.length === 0
              ? []
              : [
                  { type: 'heading' as const, id: group.id, name: group.name },
                  ...visible.map((service) => ({
                    type: 'service' as const,
                    service,
                    groupId: group.id,
                  })),
                ];
          }),
          ...getUngroupedServices({ services: gridServices, groups }).map(
            (service) => ({ type: 'service' as const, service }),
          ),
        ]
      : gridServices.map((service) => ({ type: 'service', service }));
    if (giftCardEnabled) {
      rows.push({ type: 'giftCard' });
    }
    return rows;
  }, [gridServices, gridServiceIds, groups, giftCardEnabled]);

  const listRef = useRef<FlatList<ServiceListV2Item>>(null);

  // A newly picked service lands in the selection block at the top of the list,
  // so the scroll waits for the selection itself rather than for the tap. A pick
  // that replaces the only selected service leaves the count unchanged.
  const selectionKey = [selectedServiceId, ...(additionalServicesIds ?? [])]
    .filter(Boolean)
    .join(',');
  const previousSelectionKey = useRef(selectionKey);
  useEffect(() => {
    const countOf = (key: string) => (key ? key.split(',').length : 0);
    const isPick =
      selectionKey !== previousSelectionKey.current &&
      countOf(selectionKey) >= countOf(previousSelectionKey.current);
    if (hideCategorySelect && isPick) {
      listRef.current?.scrollToOffset({ offset: 0, animated: true });
    }
    previousSelectionKey.current = selectionKey;
  }, [selectionKey, hideCategorySelect]);

  const renderItem = useCallback(
    ({ item, index: itemIndex }: ListRenderItemInfo<ServiceListV2Item>) => {
      if (item.type === 'giftCard') {
        return (
          <Animated.View
            entering={
              hasSelectedServiceRef.current
                ? undefined
                : FadeIn.delay(50 * itemIndex)
            }
          >
            <GiftCardOption
              onPress={onGiftCardPress}
              testID="gift-card-service-footer"
            />
          </Animated.View>
        );
      }

      if (item.type === 'heading') {
        return (
          <ServiceGroupHeading
            name={item.name}
            testID={`service-group-heading-${item.id}`}
          />
        );
      }

      const serviceItem = item.service;

      return (
        <Animated.View entering={FadeIn.delay(50 * itemIndex)}>
          <Box testID={`service-card-container-${serviceItem.id}`}>
            <ServiceCardV2
              service={serviceItem}
              onPress={() => onServiceSelect(serviceItem)}
              testID={`service-card-${itemIndex}`}
              currency={currency}
              showPriceRangeTooltip={showPriceRangeTooltip}
            />
          </Box>
        </Animated.View>
      );
    },
    [onServiceSelect, onGiftCardPress, currency, showPriceRangeTooltip],
  );

  const keyExtractor = useCallback(
    (item: ServiceListV2Item) =>
      item.type === 'giftCard'
        ? 'gift-card'
        : item.type === 'heading'
          ? `heading:${item.id}`
          : item.groupId
            ? `${item.groupId}:${item.service.id}`
            : item.service.id,
    [],
  );

  const listHeader = useMemo(() => {
    const hasPrimarySection = primarySectionServices.length > 0;
    const hasAddonSection = addonSectionServices.length > 0;
    const hasSelectedServices = hasPrimarySection || hasAddonSection;
    const hasUnselectedRows = gridData.some(({ type }) => type !== 'giftCard');
    const hasCategories =
      !hideCategorySelect && !groups && augmentedCategories.length > 0;

    if (!hasSelectedServices && !hasCategories) return null;

    return (
      <View>
        {hasPrimarySection && (
          <>
            <View style={styles.sectionTitle}>
              <Text variant="sStrong" color="textSecondary">
                {t('yourServicesTitle')}
              </Text>
            </View>
            {primarySectionServices.map((service, index) => (
              <AnimatedBox
                key={service.id}
                entering={ZoomIn.duration(200)}
                exiting={ZoomOut.duration(200)}
                style={styles.elevatedServiceCard}
                testID={`primary-service-card-container-${service.id}`}
              >
                <ServiceCardV2
                  service={service}
                  selected="primary"
                  categoryName={getCategoryName?.(service)}
                  onPress={() => handlePrimarySectionPress(service)}
                  testID={`primary-service-card-${index}`}
                  currency={currency}
                />
              </AnimatedBox>
            ))}
          </>
        )}
        {hasAddonSection && (
          <>
            <View style={styles.addonsSectionTitle}>
              <Text variant="sStrong" color="textSecondary">
                {t('yourAddonsTitle')}
              </Text>
            </View>
            {addonSectionServices.map((service, index) => (
              <AnimatedBox
                key={service.id}
                entering={ZoomIn.duration(200)}
                exiting={ZoomOut.duration(200)}
                style={styles.elevatedServiceCard}
                testID={`addon-service-card-container-${service.id}`}
              >
                <ServiceCardV2
                  service={service}
                  selected="addon"
                  categoryName={getCategoryName?.(service)}
                  onPress={() => onServiceSelect(service)}
                  testID={`addon-service-card-${index}`}
                  currency={currency}
                />
              </AnimatedBox>
            ))}
          </>
        )}
        {hasSelectedServices && hasUnselectedRows && (
          <>
            <View
              style={styles.sectionDivider}
              testID={`${testID}-selection-divider`}
            />
            {!groups && (
              <View style={styles.otherServicesTitle}>
                <Text variant="sStrong" color="textSecondary">
                  {fixedCategoryName ?? t('otherServicesTitle')}
                </Text>
              </View>
            )}
          </>
        )}
        {hasCategories && (
          <View style={styles.categorySection}>
            <CategorySelect
              categories={augmentedCategories}
              selectedCategoryId={selectedCategoryId}
              onCategoryChange={onCategoryChange}
              placeholder={t('categoryFilterPlaceholder')}
              testID={`${testID}-category-select`}
            />
          </View>
        )}
      </View>
    );
  }, [
    primarySectionServices,
    handlePrimarySectionPress,
    addonSectionServices,
    onServiceSelect,
    augmentedCategories,
    hideCategorySelect,
    groups,
    getCategoryName,
    selectedCategoryId,
    onCategoryChange,
    gridData,
    fixedCategoryName,
    t,
    testID,
    currency,
  ]);

  const listEmpty = useMemo(() => {
    if (primarySectionServices.length > 0 || addonSectionServices.length > 0)
      return null;
    const isFiltered = selectedCategoryId !== undefined;
    const title = isFiltered
      ? t('empty.filtered.title')
      : t('empty.unfiltered.title');
    const message = isFiltered
      ? t('empty.filtered.message')
      : t('empty.unfiltered.message');

    return (
      <View style={styles.emptyStateContainer}>
        <InfoCircleIcon
          size={64}
          color="iconTertiary"
          style={styles.emptyStateIcon}
        />
        <Heading variant="s" style={styles.emptyStateTitle}>
          {title}
        </Heading>
        <Text variant="m" style={styles.emptyStateMessage}>
          {message}
        </Text>
      </View>
    );
  }, [
    primarySectionServices.length,
    addonSectionServices.length,
    selectedCategoryId,
    t,
  ]);

  if (isLoading) {
    return <ServiceGridSkeletonV2 testID={`${testID}-loading`} />;
  }

  if (error) {
    return (
      <View
        style={[styles.container, style]}
        testID={`${testID}-error`}
        {...props}
      >
        <View style={styles.errorContainer}>
          <InfoCircleIcon
            size={64}
            color="iconNegative"
            style={styles.errorIcon}
          />
          <Heading variant="s" style={styles.errorTitle}>
            {t('error.title')}
          </Heading>
          <Text variant="m" style={styles.errorMessage}>
            {t('error.message')}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.container, style]} testID={testID} {...props}>
      <View style={styles.gridSection}>
        <Animated.FlatList
          ref={listRef}
          data={gridData}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          bounces={false}
          testID={`${testID}-list`}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            styles.flatListContent,
            hideCategorySelect && styles.fixedCategoryContent,
            selectedServiceId && styles.viewOrderBarPadding,
          ]}
          ListHeaderComponent={listHeader}
          ListEmptyComponent={listEmpty}
        />
      </View>
    </View>
  );
}

ServiceListV2.displayName = 'ServiceListV2';
